import { DeployCountry, Language } from "@types";

/**
 * Default flag URLs for each language (used when no country-specific override exists)
 */
const DEFAULT_LANGUAGE_FLAGS: Record<Language, string> = {
  [Language.EN]: "/images/flags/flag-gb.svg",
  [Language.TH]: "/images/flags/flag-th.svg",
  [Language.ID]: "/images/flags/flag-id.svg",
  [Language.BN]: "/images/flags/flag-bd.svg",
  [Language.UR]: "/images/flags/flag-pk.svg",
  [Language.TL]: "/images/flags/flag-ph.svg",
  [Language.ES]: "/images/flags/flag-mx.svg", // Default to Mexico, but can be overridden by country
  [Language.PT]: "/images/flags/flag-br.svg", // Default to Brazil, but can be overridden by country
  [Language.HI]: "/images/flags/flag-in.svg",
  [Language.AR]: "/images/flags/flag-eg.svg",
};

/**
 * Country-specific flag overrides for shared languages
 * When a language is used by multiple countries, this mapping ensures
 * the correct flag is displayed based on the deployment country
 *
 * Structure: country -> language -> flag URL
 * Only include entries that differ from DEFAULT_LANGUAGE_FLAGS
 */
const COUNTRY_LANGUAGE_FLAG_OVERRIDES: Record<DeployCountry, Partial<Record<Language, string>>> = {
  [DeployCountry.TH]: {},
  [DeployCountry.PH]: {},
  [DeployCountry.ID]: {},
  [DeployCountry.BD]: {},
  [DeployCountry.PK]: {},
  [DeployCountry.IN]: {},
  [DeployCountry.MX]: {
    [Language.ES]: "/images/flags/flag-mx.svg",
  },
  [DeployCountry.BR]: {
    [Language.PT]: "/images/flags/flag-br.svg",
  },
  [DeployCountry.PE]: {
    [Language.ES]: "/images/flags/flag-pe.svg",
  },
  [DeployCountry.EG]: {
    [Language.AR]: "/images/flags/flag-eg.svg",
  },
  [DeployCountry.CO]: {
    [Language.ES]: "/images/flags/flag-co.svg",
  },
};

/**
 * Get the appropriate flag URL for a language based on the deployment country
 *
 * Resolution priority:
 * 1. Country-specific override (if country and language combination exists)
 * 2. Default language flag (from DEFAULT_LANGUAGE_FLAGS)
 * 3. undefined (if language not found)
 *
 * @param countryCode - The deployment country code (e.g., "pe", "mx"). Optional - if not provided, returns default language flag
 * @param languageCode - The language code (e.g., "es", "pt")
 * @returns The flag URL or undefined if language not found
 */
export function getLocaleFlagUrl(
  countryCode: string | undefined,
  languageCode: string | undefined,
): string | undefined {
  if (!languageCode) {
    return undefined;
  }

  const language = languageCode.toLowerCase() as Language;

  // Check if language exists in default mapping
  if (!(language in DEFAULT_LANGUAGE_FLAGS)) {
    return undefined;
  }

  // If country code provided, check for country-specific override first
  if (countryCode) {
    const country = countryCode.toLowerCase() as DeployCountry;

    // Check if country exists in overrides map and has a specific flag for this language
    if (country in COUNTRY_LANGUAGE_FLAG_OVERRIDES) {
      const countryOverride = COUNTRY_LANGUAGE_FLAG_OVERRIDES[country]?.[language];
      if (countryOverride) {
        return countryOverride;
      }
    }
  }

  // Fall back to default language flag
  return DEFAULT_LANGUAGE_FLAGS[language];
}

/**
 * Check if a country-language combination has a specific flag override
 * (different from the default language flag)
 * @param countryCode - The deployment country code
 * @param languageCode - The language code
 * @returns True if a specific flag override exists
 */
export function hasLocaleFlagOverride(countryCode: string | undefined, languageCode: string | undefined): boolean {
  if (!countryCode || !languageCode) {
    return false;
  }

  const country = countryCode.toLowerCase() as DeployCountry;
  const language = languageCode.toLowerCase() as Language;

  return country in COUNTRY_LANGUAGE_FLAG_OVERRIDES && language in (COUNTRY_LANGUAGE_FLAG_OVERRIDES[country] ?? {});
}
